import * as React from "react"
import {
  type CategoryType,
  DirectionFromOrigin,
  type PartyRole,
  SummaryDimension,
  useSummarizeTransactionsByTimeByPartyByCategoryQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import {
  type TopActivity,
  TopActivityContainer,
  useTopActivitySortMode,
} from "src/frontend/components/OS/Shared/Widgets/TopActivities/shared"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { ComponentHeaderTitle } from "src/frontend/components/Shared/Layout/Components/Headers/ComponentSummary"
import { TopPartiesList } from "src/frontend/components/Shared/Layout/Components/Lists/TopPartiesList"
import { ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import { type PartyTransactionSummary } from "src/frontend/types"
import { FrontendPartyRole } from "src/frontend/types/FrontendPartyRole"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
  INTERFACES
*/

interface TopPartiesProps {
  highlight: TopActivity
  categoryTypes: CategoryType[]
  partyRole: PartyRole
}

/*
  COMPONENTS
*/

export const TopParties: React.FC<TopPartiesProps> = ({ highlight, partyRole, categoryTypes }) => {
  const origin = useIntervalOrigin()
  const { partiesSummaries, loading } = usePartiesSummariesQuery(
    highlight,
    partyRole,
    categoryTypes
  )
  const periodName = dateTimeHelper.displayNameForIntervalOriginRange(origin, { delimiter: " - " })

  return (
    <TopActivityContainer>
      <ComponentHeaderTitle
        title={`Top ${FrontendPartyRole.findByRole(partyRole).pluralDisplayName}`}
        componentSize={ComponentSize.Small}
        periodName={periodName}
      />
      <TopPartiesList summaries={partiesSummaries} loading={loading} />
    </TopActivityContainer>
  )
}

/*
  HOOKS
*/
function usePartiesSummariesQuery(
  highlight: TopActivity,
  partyRole: PartyRole,
  categoryTypes: CategoryType[]
) {
  const viewKey = useViewVersion()
  const intervalOrigin = useIntervalOrigin()
  const sort = useTopActivitySortMode(highlight, categoryTypes)

  const { data, loading } = useSummarizeTransactionsByTimeByPartyByCategoryQuery({
    variables: {
      direction: DirectionFromOrigin.Past,
      filter: {
        viewKey,
        categoryTypes,
        partyRole: partyRole,
        withoutCategoryAncestors: true,
      },
      origin: {
        ...intervalOrigin,
        intervalCount: 1,
      },
      sort,
      limit: 5,
      masks: [[SummaryDimension.Time, SummaryDimension.Party]],
    },
  })

  return React.useMemo(
    () => ({
      partiesSummaries:
        data?.dimensionalTransactionSummary.time[0]?.by.parties.map<PartyTransactionSummary>(
          (p) => ({
            party: p.party,
            summary: p.summary,
            categoryNames: p.by.categories.map((c) => c.category.name).sort(),
          })
        ),
      loading,
    }),
    [data, loading]
  )
}
